import { useState, useMemo } from 'react'
import { DateTime } from 'luxon'
import { Divider } from 'semantic-ui-react'
import { Card, Text, Button, Icon } from '../../../components/BaseComponents'
import FileUploadModal from '../../../components/FileUpload/FileUploadModal'
import { UploadDocumentType } from '../../../constants/businessConstants'
import styles from './styles.module.scss'
import { UserDocument } from '../../UserDocuments/userDocuments.slice'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'
import { EditableStatementTable } from '../EditableStatementTable'

interface ImonthYear {
  monthYear: string
  monthYearLabel: string
}

interface IStatementAccount {
  [name: string]: {
    account: string
    accountId: number
    monthList: ImonthYear[]
  }
}

interface IAccount {
  name: string
  mask: string | null
  logo: string
  financial_institution_name: string | null
}

export const MissingStatementMultipleUploadCard = ({
  statementMonth,
  accountObject,
  statements,
}: {
  statementMonth: IStatementAccount
  accountObject: IAccount
  statements: UserDocument[]
}) => {
  const [uploadedStatements, setUploadedStatements] = useState<UserDocument[]>(
    []
  )
  const [openUpload, setOpenUpload] = useState(false)

  const { filteredStatements, monthList } = useMemo(() => {
    const monthList = Array.from(
      new Set(
        Object.values(statementMonth).flatMap((account) =>
          account.monthList.map((month) =>
            DateTime.fromISO(month.monthYear).toFormat('MMMM yyyy')
          )
        )
      )
    )

    const accountIds = Object.values(statementMonth).flatMap(
      (account) => account.accountId
    )

    const filteredStatements = statements.filter((statement) =>
      statement.statement?.statementFinancialAccounts?.some((account) =>
        accountIds.includes(account.financialAccountId)
      )
    )
    return {
      monthList,
      filteredStatements,
    }
  }, [statementMonth, statements])

  const setUploadedFile = ({
    doc,
  }: {
    fileName?: string
    id?: number
    doc?: UserDocument
  }) => {
    const statements = uploadedStatements
    if (doc?.id) {
      statements.push(doc)
      setUploadedStatements(statements)
    }
  }

  return (
    <>
      {Object.entries(statementMonth).map(([account, value]) => (
        <div key={account} className={styles.accountIconGrid}>
          {value.account.toLowerCase().includes('credit') && (
            <Icon color="gray" icon={regular('credit-card')} size="2x" />
          )}
          {!value.account.toLowerCase().includes('credit') && (
            <Icon color="gray" icon={regular('bank')} size="2x" />
          )}
          <div className={styles.accountName}>{value.account}</div>
        </div>
      ))}
      <Divider />
      <Card
        key={'month'}
        backgroundColor={'stone40'}
        className={styles.bodyCard}
      >
        <div className={styles.accountCard}>
          <div className={styles.topRow}>
            <div className={styles.multipleUploadGrid}>
              <Text as="h2">
                {accountObject.financial_institution_name} statements
              </Text>
              <br />
              <Text as="bodySm" className={styles.uploadText}>
                Upload all {accountObject.financial_institution_name}{' '}
                statement(s) for the accounts above, covering the months listed
                here:
              </Text>
              <ul>
                {monthList.map((value) => (
                  <li key={`${value}-list`} className={styles.uploadListItem}>
                    <Text as="bodySm">{value}</Text>
                  </li>
                ))}
              </ul>
            </div>
            <div className={styles.uploadGrid}>
              <div className={styles.uploadButton}>
                <Button
                  onClick={() => setOpenUpload(true)}
                  className={styles.uploadBtn}
                  variant="secondary"
                  fullWidth
                >
                  <Icon
                    icon={regular('upload')}
                    size="lg"
                    className={styles.uploadIcon}
                  />
                  Upload
                </Button>
              </div>
            </div>
          </div>
        </div>
        <FileUploadModal
          documentType={UploadDocumentType.STATEMENT}
          open={openUpload}
          close={() => setOpenUpload(false)}
          userFacing
          monthYear={undefined}
          setUploadedFile={setUploadedFile}
        />
      </Card>
      <div className={styles.fileListCard}>
        {Boolean(filteredStatements.length) && (
          <EditableStatementTable documents={filteredStatements} />
        )}
      </div>
    </>
  )
}
